<template>
  <div class="oai-InviteStep_AdminFormWrapper">
    <div class="oai-InviteStep_AdminForm">
      <FormFieldNext :label="$t('onboarding.existed_admins')" class="oai-InviteStep_AdminField">
        <div class="oai-InviteStep_AdminSelect">
          <AppSelect
            :disabled-items="disabledAdminItems"
            :loading="isAddAdminsLoading"
            :model-value="adminFormModel.accountId"
            :offset="[0, '-100%']"
            :options="usersAsOptions"
            :placeholder="$t('placeholder.search')"
            :search-function="getPlatformUsers"
            append-to=".oai-InviteStep_AdminSelect"
            dropdown-search
            item-label="name"
            item-value="accountId"
            skeleton-loader
            @update:options="allUsers = $event"
            @update:model-value="onAddAdmin"
          >
            <template #option-label="{ option }">
              <OwnerFieldOption
                :already-added="option.alreadyAdded"
                :option="option"
                label-key="name"
              >
                <template v-if="option.alreadyAdded" #badge>
                  {{ $t('badge.already_added') }}
                </template>
              </OwnerFieldOption>
            </template>

            <template #button-content="{ option }">
              <template v-if="option">
                <OwnerFieldOption :option="option" label-key="name" />
              </template>
              <template v-else>
                {{ $t('admin.user') }}
              </template>
            </template>
          </AppSelect>
        </div>
      </FormFieldNext>

      <AppTable
        :columns="ADMIN_COLUMNS"
        :data="admins"
        :loading="isAdminsLoading"
        class="oai-InviteStep_AdminTable"
        no-border
        offset-left="16px"
        offset-right="16px"
        style="--head-padding-bottom: 6px; --row-border-radius: 6px"
        type="primary-next"
      >
        <template #header-cell="{ column }">
          <template v-if="column.title">
            {{ $t(column.title) }}
          </template>
        </template>
        <template #cell="{ columnKey, item }">
          <template v-if="columnKey === TABLE_COLUMNS_KEYS.NAME">
            <UserNameCell :item="item" item-label="displayName" />
          </template>

          <template v-else-if="columnKey === TABLE_COLUMNS_KEYS.ACTION">
            <AppButton
              class="oai-InviteStep_DeleteAdminBtn"
              icon="delete-next"
              size="sm"
              type="tertiary-next"
              @click="onRemoveAdminClick(item)"
            />
          </template>
        </template>

        <template #loading>
          <PluginAdminsTableLoader />
        </template>
      </AppTable>

      <portal to="modal-windows">
        <AdminDeleteModal
          :admin-to-delete="adminToDelete"
          :show="isShowAdminDeleteModal"
          @on-close="isShowAdminDeleteModal = false"
          @on-confirm="onDeleteAdmin"
        />
      </portal>
    </div>
    <div class="oai-InviteStep_Actions">
      <AppButtonNext
        :disabled="isSubmitting || isNavigating"
        class="oai-InviteStep_SubmitAction"
        icon-after="arrow-right-simple-next"
        type="primary-next"
        @click="handleContinue"
      >
        {{ $t('action.continue') }}
      </AppButtonNext>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref } from 'vue'

import InstanceAdministratorsApiHandler from '@/api/instance-administrators'
import PlatformApiHandler from '@/api/platform'
import { useOnboardingAiStepContext } from '@/composables/onboarding-ai-step'
import { tracker } from '@/tracking/amplitude'
import { EVENT_CATEGORIES, EVENT_SOURCES } from '@/tracking/amplitude-helpers'
import { handleAdministratorsList } from '@/utils/administrators'
import { REQUEST_ENTITY_KEYS } from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { TABLE_COLUMNS_KEYS } from '@/utils/table-columns'

import FormFieldNext from '@/components/form/FormFieldNext'
import OwnerFieldOption from '@/components/form/OwnerFieldOption'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppButtonNext from '@/components/ui/AppButtonNext/AppButtonNext'
import AppSelect from '@/components/ui/AppSelect/AppSelect'
import PluginAdminsTableLoader from '@/components/ui/SkeletonLoaders/PluginAdminsTableLoader'
import AppTable from '@/components/ui/Table/AppTable'
import AdminDeleteModal from '@/components/workspaces/AdminDeleteModal'
import UserNameCell from '@/views/workspaces/settings/plugin-users/UserNameCell'

const emit = defineEmits({
  next: null
})

const stepContext = useOnboardingAiStepContext()
const isNavigating = computed(() => Boolean(stepContext?.navigating))

const isSubmitting = ref(false)

const adminFormModel = ref({ accountId: '' })
const disabledAdminItems = computed(() => admins.value.map(admin => admin.accountId))

const usersAsOptions = computed(() => {
  return handleAdministratorsList({
    allAdministrators: admins.value,
    usersList: allUsers.value
  })
})

const handleContinue = () => {
  emit('next', {
    invitees_count: admins.value.length
  })
}

const isAddAdminsLoading = ref(false)

const ADMIN_COLUMNS = [
  {
    title: 'onboarding.invite_admins',
    key: TABLE_COLUMNS_KEYS.NAME,
    width: 'auto'
  },
  {
    key: TABLE_COLUMNS_KEYS.ACTION,
    width: 24
  }
]

const onAddAdmin = async accountId => {
  const adminsApi = new InstanceAdministratorsApiHandler()

  try {
    isAddAdminsLoading.value = true

    await adminsApi.addAdmin({
      accountId
    })

    admins.value = []
    handleAdminTracking()
    await getAdmins()
  } catch (error) {
    handleError({ error })
  } finally {
    isAddAdminsLoading.value = false
  }
}

const handleAdminTracking = (isAdded = true) => {
  const eventName = isAdded ? 'administrator added' : 'administrator removed'

  tracker.logEvent(eventName, {
    category: EVENT_CATEGORIES.ONBOARDING,
    source: EVENT_SOURCES.ONBOARDING
  })
}

const adminToDelete = ref(null)
const isShowAdminDeleteModal = ref(false)

const onRemoveAdminClick = admin => {
  adminToDelete.value = admin
  nextTick(() => {
    isShowAdminDeleteModal.value = true
  })
}

const onDeleteAdmin = async () => {
  const adminsApi = new InstanceAdministratorsApiHandler()

  try {
    await adminsApi.removeAdmin({
      accountId: adminToDelete.value.accountId
    })
    isShowAdminDeleteModal.value = false
    adminToDelete.value = null
    admins.value = []
    handleAdminTracking(false)
    await getAdmins()
  } catch (error) {
    handleError({ error })
  }
}

const admins = ref([])
const isAdminsLoading = ref(false)

const getAdmins = async () => {
  const adminsApi = new InstanceAdministratorsApiHandler()

  try {
    isAdminsLoading.value = true
    admins.value = await adminsApi.getAdmins()
  } catch (error) {
    handleError({ error })
  } finally {
    isAdminsLoading.value = false
  }
}

const allUsers = ref([])

const getPlatformUsers = async (searchString = null) => {
  const platformApi = new PlatformApiHandler()

  let users = []

  try {
    users = await platformApi.getPlatformUsers({ searchString, [REQUEST_ENTITY_KEYS.LIMIT]: 50 })
  } catch (error) {
    handleError({ error })
  }

  return handleAdministratorsList({
    allAdministrators: admins.value,
    usersList: users
  })
}

onMounted(async () => {
  await getAdmins()
  allUsers.value = await getPlatformUsers()
})
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/onboarding-ai-step';
@import '~@/assets/styles/app-table-helpers';

.oai-InviteStep_AdminFormWrapper {
  @extend %oai-wizard-step-shell;
}

.oai-InviteStep_AdminForm {
  display: grid;
  gap: 16px;
  width: 100%;
}

.oai-InviteStep_AdminField,
.oai-InviteStep_AdminTable {
  width: 100%;
}

.oai-InviteStep_AdminSelect {
  width: 100%;
  position: relative;
}

.oai-InviteStep_DeleteAdminBtn {
  @extend %app-table-hidden-items;
}

.oai-InviteStep_Actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 32px;
  width: 100%;
}

.oai-InviteStep_SubmitAction {
  min-height: 40px;
  padding: 6px 12px;
}
</style>
